import React, { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { uploadTemplateBg, createTemplate, updateTemplate } from '../utils/api';

// ── Constants ─────────────────────────────────────────────────────────────────
const PROFILE_FIELDS = [
  { key:'name',        label:'Name' },
  { key:'prnCode',     label:'PRN Code' },
  { key:'dob',         label:'Date of Birth' },
  { key:'class',       label:'Class' },
  { key:'mobile',      label:'Mobile' },
  { key:'address',     label:'Address' },
  { key:'bloodGroup',  label:'Blood Group' },
  { key:'fatherName',  label:"Father Name" },
  { key:'motherName',  label:"Mother Name" },
  { key:'designation', label:'Designation' },
  { key:'employeeCode',label:'Employee Code' },
  { key:'department',  label:'Department' },
  { key:'aadhar',      label:'Aadhar' },
  { key:'pan',         label:'PAN' },
  { key:'rfidCode',    label:'RFID Code' },
  { key:'barCode',     label:'Bar Code' },
  { key:'qrCode',      label:'QR Code' },
];

const FONT_GROUPS = [
  { group: '── System Fonts ──', fonts: [
    'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New',
    'Verdana', 'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS',
    'Palatino', 'Garamond', 'Bookman', 'Arial Black', 'Century Gothic',
  ]},
  { group: '── Sans Serif ──', fonts: [
    'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald',
    'Raleway', 'Poppins', 'Nunito', 'Ubuntu', 'Source Sans Pro',
    'Noto Sans', 'Mukta', 'Work Sans', 'Barlow', 'Inter',
    'DM Sans', 'Outfit', 'Plus Jakarta Sans', 'Manrope', 'Figtree',
  ]},
  { group: '── Serif ──', fonts: [
    'Playfair Display', 'Merriweather', 'Lora', 'PT Serif', 'Crimson Text',
    'EB Garamond', 'Cormorant Garamond', 'Libre Baskerville', 'Cardo', 'Bitter',
    'Domine', 'Spectral', 'Zilla Slab', 'Arvo', 'Rokkitt',
  ]},
  { group: '── Display / Decorative ──', fonts: [
    'Bebas Neue', 'Anton', 'Righteous', 'Exo 2', 'Rajdhani',
    'Teko', 'Russo One', 'Titillium Web', 'Yantramanav', 'Saira',
    'Fjalla One', 'Quantico', 'Orbitron', 'Audiowide', 'Michroma',
  ]},
  { group: '── Handwriting / Script ──', fonts: [
    'Pacifico', 'Dancing Script', 'Satisfy', 'Great Vibes', 'Caveat',
    'Shadows Into Light', 'Kalam', 'Permanent Marker', 'Patrick Hand', 'Indie Flower',
    'Sacramento', 'Allura', 'Alex Brush', 'Pinyon Script', 'Lobster',
  ]},
  { group: '── Marathi / Devanagari ──', fonts: [
    'Noto Sans Devanagari', 'Tiro Devanagari Hindi', 'Baloo 2', 'Mukta',
    'Laila', 'Hind', 'Poppins', 'Rozha One', 'Yatra One', 'Tillana',
  ]},
  { group: '── Urdu / Arabic ──', fonts: [
    'Noto Nastaliq Urdu', 'Noto Sans Arabic', 'Amiri', 'Lateef',
    'Scheherazade New', 'Markazi Text',
  ]},
];

const FONTS = FONT_GROUPS.flatMap(g => g.fonts);

const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=' + [
  'Roboto','Open+Sans','Lato','Montserrat','Oswald','Raleway','Poppins','Nunito',
  'Ubuntu','Source+Sans+Pro','Noto+Sans','Mukta','Work+Sans','Barlow','Inter',
  'DM+Sans','Outfit','Plus+Jakarta+Sans','Manrope','Figtree',
  'Playfair+Display','Merriweather','Lora','PT+Serif','Crimson+Text',
  'EB+Garamond','Cormorant+Garamond','Libre+Baskerville','Cardo','Bitter',
  'Domine','Spectral','Zilla+Slab','Arvo','Rokkitt',
  'Bebas+Neue','Anton','Righteous','Exo+2','Rajdhani','Teko','Russo+One',
  'Titillium+Web','Yantramanav','Saira','Fjalla+One','Quantico','Orbitron',
  'Audiowide','Michroma',
  'Pacifico','Dancing+Script','Satisfy','Great+Vibes','Caveat',
  'Shadows+Into+Light','Kalam','Permanent+Marker','Patrick+Hand','Indie+Flower',
  'Sacramento','Allura','Alex+Brush','Pinyon+Script','Lobster',
  'Noto+Sans+Devanagari','Tiro+Devanagari+Hindi','Baloo+2','Laila','Hind',
  'Rozha+One','Yatra+One','Tillana',
  'Noto+Nastaliq+Urdu','Noto+Sans+Arabic','Amiri','Lateef',
  'Scheherazade+New','Markazi+Text',
].map(f => f+':wght@300;400;500;600;700').join('&family=') + '&display=swap';

const ELEMENT_TYPES = [
  { type:'text',      icon:'T',  label:'Text Field' },
  { type:'photo',     icon:'👤', label:'Photo' },
  { type:'signature', icon:'✍️', label:'Signature' },
  { type:'barcode',   icon:'▌▌', label:'Barcode' },
  { type:'qrcode',    icon:'▦',  label:'QR Code' },
  { type:'shape',     icon:'□',  label:'Shape/Box' },
];

const BASE_URL = process.env.REACT_APP_API_URL || '';

const CARD_PRESETS = [
  { label:'CR80 — ID Card (85.6×54)',  w:85.6,  h:53.98 },
  { label:'CR79 — Slightly smaller',   w:83.9,  h:53.0  },
  { label:'A4 Portrait (210×297)',      w:210,   h:297   },
  { label:'A4 Landscape (297×210)',     w:297,   h:210   },
  { label:'A5 Portrait (148×210)',      w:148,   h:210   },
  { label:'A6 (105×148)',               w:105,   h:148   },
  { label:'Square 90×90',              w:90,    h:90    },
  { label:'Custom…',                   w:null,  h:null  },
];

let eidCounter = 1;
const newId = () => `el_${Date.now()}_${eidCounter++}`;

const DEFAULT_ELEMENTS = {
  text:      { type:'text',      w:30, h:8,  fontSize:10, fontFamily:'Arial', fontColor:'#000000', fontWeight:'normal', fontStyle:'normal', textAlign:'left', field:'name', staticText:'', shape:'rectangle', bgColor:'transparent', borderColor:'transparent', borderWidth:0, rotation:0, zIndex:0 },
  photo:     { type:'photo',     w:20, h:25, shape:'circle',    bgColor:'#eeeeee', borderColor:'#cccccc', borderWidth:1, rotation:0, zIndex:0 },
  signature: { type:'signature', w:30, h:12, shape:'rectangle', bgColor:'transparent', borderColor:'#aaaaaa', borderWidth:1, rotation:0, zIndex:0 },
  barcode:   { type:'barcode',   w:35, h:12, field:'prnCode',   shape:'rectangle', bgColor:'#ffffff', borderColor:'transparent', borderWidth:0, rotation:0, zIndex:0 },
  qrcode:    { type:'qrcode',    w:18, h:18, field:'qrCode',    shape:'rectangle', bgColor:'#ffffff', borderColor:'transparent', borderWidth:0, rotation:0, zIndex:0 },
  shape:     { type:'shape',     w:40, h:15, shape:'rectangle', bgColor:'#e3e3e3', borderColor:'#999999', borderWidth:1, rotation:0, zIndex:0 },
};

// ── Sub-components ────────────────────────────────────────────────────────────
function ColorPicker({ value, onChange, label }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      {label && <span style={{fontSize:'.78rem',color:'var(--text-muted)',minWidth:60}}>{label}</span>}
      <input type="color" value={value||'#000000'} onChange={e=>onChange(e.target.value)}
        style={{width:32,height:24,padding:1,border:'1px solid var(--border)',borderRadius:4,cursor:'pointer',background:'none'}}/>
      <input type="text" value={value||''} onChange={e=>onChange(e.target.value)}
        style={{width:72,fontFamily:'monospace',fontSize:'.78rem',padding:'2px 6px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:4,color:'var(--text)'}}/>
    </div>
  );
}

function PropRow({ label, children }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:'1px solid var(--border)'}}>
      <span style={{fontSize:'.77rem',color:'var(--text-muted)',minWidth:80,flexShrink:0}}>{label}</span>
      <div style={{flex:1}}>{children}</div>
    </div>
  );
}

function getClipStyle(shape) {
  if (shape === 'circle')    return { borderRadius:'50%' };
  if (shape === 'rounded')   return { borderRadius:'8px' };
  if (shape === 'pentagon')  return { clipPath:'polygon(50% 0%,100% 38%,82% 100%,18% 100%,0% 38%)' };
  if (shape === 'hexagon')   return { clipPath:'polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%)' };
  if (shape === 'octagon')   return { clipPath:'polygon(30% 0%,70% 0%,100% 30%,100% 70%,70% 100%,30% 100%,0% 70%,0% 30%)' };
  return {};
}

// ── Main component ────────────────────────────────────────────────────────────
// export default function CardDesigner({ template, onSave, onClose, previewProfile, BASE_URL: burl }) 
export default function CardDesigner({ 
  template, onSave, onClose, previewProfile, BASE_URL: burl, targetUsername 
}){
  const BU = burl || BASE_URL;

  const [name,        setName]        = useState(template?.name || 'Untitled');
  const [bgColor,     setBgColor]     = useState(template?.bgColor || '#ffffff');
  const [bgImage,     setBgImage]     = useState(template?.bgImagePath ? `${BASE_URL}${template.bgImagePath}` : null);
  const [bgImagePath, setBgImagePath] = useState(template?.bgImagePath || null);
  const [bgUploading, setBgUploading] = useState(false);
  const [savedTmplId, setSavedTmplId] = useState(template?._id || null);
  const [elements,    setElements]    = useState(template?.elements || []);
  const [selected,    setSelected]    = useState(null);
  const [cardW,       setCardW]       = useState(template?.cardWidth  || 85.6);
  const [cardH,       setCardH]       = useState(template?.cardHeight || 53.98);
  const [cardSide,    setCardSide]    = useState(template?.cardSide || 'front');
  const [saving,      setSaving]      = useState(false);
  const [zoom,        setZoom]        = useState(1);

  const canvasRef  = useRef(null);
  const bgInputRef = useRef(null);
  const dragRef    = useRef(null);

  const sel = elements.find(e => e.id === selected);

  // ── Load Google Fonts ────────────────────────────────────────────────────────
  useEffect(() => {
    const id = 'card-designer-gfonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id   = id;
      link.rel  = 'stylesheet';
      link.href = GOOGLE_FONTS_URL;
      document.head.appendChild(link);
    }
  }, []);

  // ── Zoom to fit ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const container = canvasRef.current?.parentElement;
    if (!container) return;
    const availW = container.clientWidth - 280 - 48;
    const mmToPx = (mm) => mm * 3.7795;
    const scale  = Math.min(1, availW / mmToPx(cardW));
    setZoom(Math.max(0.5, scale));
  }, [cardW]);

  // ── Add element ──────────────────────────────────────────────────────────────
  const addElement = useCallback((type) => {
    const el = {
      ...DEFAULT_ELEMENTS[type],
      id: newId(),
      x: 5, y: 5,
      zIndex: elements.length,
    };
    setElements(prev => [...prev, el]);
    setSelected(el.id);
  }, [elements.length]);

  // ── Update element prop ──────────────────────────────────────────────────────
  const updateEl = useCallback((id, patch) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  }, []);

  // ── Delete selected ──────────────────────────────────────────────────────────
  const deleteSelected = useCallback(() => {
    if (!selected) return;
    setElements(prev => prev.filter(e => e.id !== selected));
    setSelected(null);
  }, [selected]);

  // ── Drag to move ─────────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e, elId) => {
    e.stopPropagation();
    setSelected(elId);
    const el = elements.find(x => x.id === elId);
    dragRef.current = {
      elId, mode: 'move',
      startX: e.clientX, startY: e.clientY,
      ox: el.x, oy: el.y,
    };
    e.preventDefault();
  }, [elements]);

  const handleResizeDown = useCallback((e, elId, corner) => {
    e.stopPropagation();
    const el = elements.find(x => x.id === elId);
    dragRef.current = {
      elId, mode: 'resize', corner,
      startX: e.clientX, startY: e.clientY,
      ox: el.x, oy: el.y, ow: el.w, oh: el.h,
    };
    e.preventDefault();
  }, [elements]);

  const handleMouseMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = (cardW) / rect.width;
    const scaleY = (cardH) / rect.height;
    const dx = (e.clientX - d.startX) * scaleX;
    const dy = (e.clientY - d.startY) * scaleY;

    if (d.mode === 'move') {
      const el = elements.find(x => x.id === d.elId);
      const nx = Math.max(0, Math.min(100 - el.w, d.ox + (dx / cardW * 100)));
      const ny = Math.max(0, Math.min(100 - el.h, d.oy + (dy / cardH * 100)));
      updateEl(d.elId, { x: parseFloat(nx.toFixed(2)), y: parseFloat(ny.toFixed(2)) });
    } else if (d.mode === 'resize') {
      const nw = Math.max(5, d.ow + (dx / cardW * 100));
      const nh = Math.max(5, d.oh + (dy / cardH * 100));
      updateEl(d.elId, { w: parseFloat(nw.toFixed(2)), h: parseFloat(nh.toFixed(2)) });
    }
  }, [elements, cardW, cardH, updateEl]);

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // ── Render element on canvas ─────────────────────────────────────────────────
  const renderElement = useCallback((el) => {
    const isSel = el.id === selected;
    const profile = previewProfile || {};
    const clipStyle = getClipStyle(el.shape);

    const wrapStyle = {
      position: 'absolute',
      left:   `${el.x}%`,
      top:    `${el.y}%`,
      width:  `${el.w}%`,
      height: `${el.h}%`,
      zIndex: el.zIndex || 0,
      transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
      cursor: 'move',
      border: isSel ? '2px solid var(--primary)' : '1px dashed rgba(150,150,150,.4)',
      boxSizing: 'border-box',
      userSelect: 'none',
    };

    let inner = null;

    if (el.type === 'text') {
      const val = el.field ? (profile[el.field] || `[${el.field}]`) : (el.staticText || 'Text');
      inner = (
        <div style={{
          width:'100%', height:'100%', overflow:'hidden',
          backgroundColor: el.bgColor !== 'transparent' ? el.bgColor : undefined,
          border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : undefined,
          display:'flex', alignItems:'center',
          padding:'0 2px',
        }}>
          {/* <span style={{
            fontSize:   `${el.fontSize}px`,
            fontFamily: el.fontFamily,
            color:      el.fontColor,
            fontWeight: el.fontWeight,
            fontStyle:  el.fontStyle,
            textAlign:  el.textAlign,
            width:'100%',
            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
          }}>{val}</span> */}
          <span style={{
  fontSize:   `${el.fontSize * zoom}px`,
  fontFamily: el.fontFamily,
  color:      el.fontColor,
  fontWeight: el.fontWeight,
  fontStyle:  el.fontStyle,
  textAlign:  el.textAlign,
  width:'100%',
  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
}}>{val}</span>
        </div>
      );
    } else if (el.type === 'photo') {
      const src = profile.photoPath ? `${BU}${profile.photoPath}` : null;
      inner = (
        <div style={{
          width:'100%', height:'100%',
          backgroundColor: el.bgColor,
          border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : undefined,
          overflow:'hidden', ...clipStyle,
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          {src
            ? <img src={src} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            : <span style={{fontSize:'1.5rem'}}>👤</span>
          }
        </div>
      );
    } else if (el.type === 'signature') {
      const src = profile.signImagePath ? `${BU}${profile.signImagePath}` : null;
      inner = (
        <div style={{
          width:'100%', height:'100%',
          backgroundColor: el.bgColor !== 'transparent' ? el.bgColor : undefined,
          border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : '1px dashed #aaa',
          display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden',
        }}>
          {src
            ? <img src={src} alt="" style={{width:'100%',height:'100%',objectFit:'contain'}}/>
            : <span style={{fontSize:'.6rem',color:'#aaa'}}>Signature</span>
          }
        </div>
      );
    } else if (el.type === 'barcode' || el.type === 'qrcode') {
      const val = el.field ? (profile[el.field] || el.field) : 'CODE';
      inner = (
        <div style={{
          width:'100%', height:'100%', backgroundColor:'#fff',
          border: '1px solid #ddd',
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
          padding:2,
        }}>
          <span style={{fontSize:'.55rem', fontFamily:'monospace', color:'#333', letterSpacing:2}}>
            {el.type==='qrcode' ? '▦ ' : '▌▌▌ '}{val}
          </span>
          {el.type==='barcode' && (
            <div style={{display:'flex',gap:1,height:12,marginTop:2}}>
              {val.split('').map((c,i)=>(
                <div key={i} style={{
                  width: c.charCodeAt(0) % 2 === 0 ? 2 : 1,
                  height:'100%', background:'#222',
                }}/>
              ))}
            </div>
          )}
        </div>
      );
    } else if (el.type === 'shape') {
      inner = (
        <div style={{
          width:'100%', height:'100%',
          backgroundColor: el.bgColor,
          border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : undefined,
          ...clipStyle,
        }}/>
      );
    }

    return (
      <div
        key={el.id}
        style={wrapStyle}
        onMouseDown={e => handleMouseDown(e, el.id)}
      >
        {inner}
        {isSel && (
          <>
            <div
              style={{position:'absolute',right:-4,bottom:-4,width:10,height:10,
                background:'var(--primary)',borderRadius:'50%',cursor:'se-resize',zIndex:100}}
              onMouseDown={e => handleResizeDown(e, el.id, 'se')}
            />
            <div
              style={{position:'absolute',top:-8,right:-8,width:16,height:16,
                background:'var(--error,#ef4444)',color:'#fff',borderRadius:'50%',
                fontSize:10,display:'flex',alignItems:'center',justifyContent:'center',
                cursor:'pointer',zIndex:101}}
              onMouseDown={e => { e.stopPropagation(); deleteSelected(); }}
            >✕</div>
          </>
        )}
      </div>
    );
}, [selected, previewProfile, BU, handleMouseDown, handleResizeDown, deleteSelected, zoom]);
  // ── Properties panel ─────────────────────────────────────────────────────────
  const renderProps = () => {
    if (!sel) return (
      <div style={{color:'var(--text-muted)',fontSize:'.82rem',textAlign:'center',padding:20}}>
        Click an element to edit its properties
      </div>
    );

    const upd = patch => updateEl(sel.id, patch);
    const isCircle = sel.shape === 'circle' && (sel.type === 'photo' || sel.type === 'shape');

    return (
      <div style={{display:'flex',flexDirection:'column',gap:2}}>
        <div style={{fontWeight:700,fontSize:'.82rem',marginBottom:8,color:'var(--primary)'}}>
          {sel.type.toUpperCase()} PROPERTIES
        </div>

        {/* Position */}
        <PropRow label="Position X">
          <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
            value={sel.x.toFixed(1)} onChange={e=>upd({x:parseFloat(e.target.value)||0})}/>
        </PropRow>
        <PropRow label="Position Y">
          <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
            value={sel.y.toFixed(1)} onChange={e=>upd({y:parseFloat(e.target.value)||0})}/>
        </PropRow>

        {/* Size — circle shows Radius only, everything else shows Width + Height */}
        {isCircle ? (
          <PropRow label="Radius %">
            <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.w.toFixed(1)}
              onChange={e => {
                const r = parseFloat(e.target.value) || 5;
                upd({ w: r, h: r });
              }}/>
          </PropRow>
        ) : (
          <>
            <PropRow label="Width %">
              <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
                value={sel.w.toFixed(1)} onChange={e=>upd({w:parseFloat(e.target.value)||5})}/>
            </PropRow>
            <PropRow label="Height %">
              <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
                value={sel.h.toFixed(1)} onChange={e=>upd({h:parseFloat(e.target.value)||5})}/>
            </PropRow>
          </>
        )}

        <PropRow label="Rotation°">
          <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
            value={sel.rotation||0} onChange={e=>upd({rotation:parseInt(e.target.value)||0})}/>
        </PropRow>
        <PropRow label="Layer (Z)">
          <input type="number" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
            value={sel.zIndex||0} onChange={e=>upd({zIndex:parseInt(e.target.value)||0})}/>
        </PropRow>

        {/* Text-specific */}
        {sel.type === 'text' && (<>
          <PropRow label="Field">
            <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.field||''} onChange={e=>upd({field:e.target.value,staticText:''})}>
              <option value="">Static text ↓</option>
              {PROFILE_FIELDS.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </PropRow>
          {!sel.field && (
            <PropRow label="Static text">
              <input className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
                value={sel.staticText||''} onChange={e=>upd({staticText:e.target.value})}/>
            </PropRow>
          )}
          <PropRow label="Font">
            <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.fontFamily} onChange={e=>upd({fontFamily:e.target.value})}>
              {FONT_GROUPS.map(g=>(
                <optgroup key={g.group} label={g.group}>
                  {g.fonts.map(f=>(
                    <option key={f} value={f} style={{fontFamily:f}}>{f}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </PropRow>
          {/* Live font preview */}
          <div style={{padding:'4px 0',borderBottom:'1px solid var(--border)'}}>
            <div style={{
              fontFamily: sel.fontFamily,
              // fontSize: Math.min(sel.fontSize||12, 16)+'px',
              fontSize: Math.min((sel.fontSize||12) * zoom, 16 * zoom)+'px',
              color: sel.fontColor||'#000',
              fontWeight: sel.fontWeight,
              fontStyle: sel.fontStyle,
              background: '#fff',
              padding: '4px 6px',
              borderRadius: 3,
              minHeight: 24,
              overflow:'hidden',
              whiteSpace:'nowrap',
            }}>
              {sel.field ? (previewProfile?.[sel.field]||'Sample Text') : (sel.staticText||'Sample Text')}
            </div>
          </div>
          <PropRow label="Size (px)">
            <input type="number" min="6" max="72" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.fontSize} onChange={e=>upd({fontSize:parseInt(e.target.value)||10})}/>
          </PropRow>
          <PropRow label="Color">
            <ColorPicker value={sel.fontColor} onChange={v=>upd({fontColor:v})}/>
          </PropRow>
          <PropRow label="Weight">
            <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.fontWeight} onChange={e=>upd({fontWeight:e.target.value})}>
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="600">Semi-bold</option>
            </select>
          </PropRow>
          <PropRow label="Style">
            <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.fontStyle} onChange={e=>upd({fontStyle:e.target.value})}>
              <option value="normal">Normal</option>
              <option value="italic">Italic</option>
            </select>
          </PropRow>
          <PropRow label="Align">
            <div style={{display:'flex',gap:4}}>
              {['left','center','right'].map(a=>(
                <button key={a} className={`btn btn-xs ${sel.textAlign===a?'btn-primary':'btn-outline'}`}
                  onClick={()=>upd({textAlign:a})}>{a[0].toUpperCase()}</button>
              ))}
            </div>
          </PropRow>
        </>)}

        {/* Photo/Shape shape picker */}
        {/* {(sel.type==='photo'||sel.type==='shape') && (
          <PropRow label="Shape">
            <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.shape} onChange={e=>upd({shape:e.target.value})}>
              {['rectangle','circle','rounded','pentagon','hexagon','octagon'].map(s=>
                <option key={s} value={s}>{s}</option>)}
            </select>
          </PropRow>
        )} */}
        {/* Photo/Shape shape picker */}
{(sel.type==='photo'||sel.type==='shape') && (
  <PropRow label="Shape">
    <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
      value={sel.shape}
      onChange={e => {
        const newShape = e.target.value;
        if (newShape === 'circle') {
          const size = Math.min(sel.w, sel.h);
          upd({ shape: newShape, w: size, h: size });
        } else {
          upd({ shape: newShape });
        }
      }}>
      {['rectangle','circle','rounded','pentagon','hexagon','octagon'].map(s=>
        <option key={s} value={s}>{s}</option>)}
    </select>
  </PropRow>
)}

        {/* Barcode/QR field */}
        {(sel.type==='barcode'||sel.type==='qrcode') && (
          <PropRow label="Field">
            <select className="form-input" style={{padding:'2px 6px',fontSize:'.78rem'}}
              value={sel.field||'prnCode'} onChange={e=>upd({field:e.target.value})}>
              {PROFILE_FIELDS.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </PropRow>
        )}

        {/* Background & border */}
        {sel.type !== 'text' && (
          <PropRow label="Fill">
            <ColorPicker value={sel.bgColor==='transparent'?'#ffffff':sel.bgColor}
              onChange={v=>upd({bgColor:v})}/>
          </PropRow>
        )}
        <PropRow label="Border px">
          <input type="number" min="0" max="10" className="form-input" style={{padding:'2px 6px',fontSize:'.78rem',width:60}}
            value={sel.borderWidth||0} onChange={e=>upd({borderWidth:parseInt(e.target.value)||0})}/>
        </PropRow>
        {(sel.borderWidth||0) > 0 && (
          <PropRow label="Border color">
            <ColorPicker value={sel.borderColor} onChange={v=>upd({borderColor:v})}/>
          </PropRow>
        )}

        <div style={{marginTop:8}}>
          <button className="btn btn-danger btn-sm" onClick={deleteSelected}>🗑️ Delete Element</button>
        </div>
      </div>
    );
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name, bgColor,
        bgImagePath: bgImagePath || null,
        elements, cardWidth: cardW, cardHeight: cardH, cardSide,
      };
      if (savedTmplId) {
        await updateTemplate(savedTmplId, payload);
        toast.success('Template saved!');
        onSave({ ...payload, _id: savedTmplId });
      } else {
        await onSave(payload);
        toast.success('Template saved!');
      }
    } catch(e) {
      toast.error(e.response?.data?.message || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const mmToPx = mm => mm * 3.7795 * zoom;

  return (
    <div style={{
      display:'flex', flexDirection:'column', height:'100%', gap:0,
      background:'var(--bg)', color:'var(--text)',
    }}>
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display:'flex', alignItems:'center', gap:8, padding:'10px 14px',
        borderBottom:'1px solid var(--border)', background:'var(--surface)',
        flexWrap:'wrap',
      }}>
        <input className="form-input" style={{maxWidth:200,padding:'4px 8px',fontSize:'.84rem'}}
          value={name} onChange={e=>setName(e.target.value)} placeholder="Template name"/>
        <select className="search-input" style={{maxWidth:120,fontSize:'.82rem'}}
          value={cardSide} onChange={e=>setCardSide(e.target.value)}>
          <option value="front">Front side</option>
          <option value="back">Back side</option>
        </select>
        <select className="search-input" style={{maxWidth:190,fontSize:'.78rem'}}
          value={CARD_PRESETS.find(p=>p.w===cardW&&p.h===cardH)?.label||'Custom…'}
          onChange={e=>{
            const p = CARD_PRESETS.find(x=>x.label===e.target.value);
            if (p && p.w) { setCardW(p.w); setCardH(p.h); }
          }}>
          {CARD_PRESETS.map(p=><option key={p.label} value={p.label}>{p.label}</option>)}
        </select>
        <div style={{display:'flex',alignItems:'center',gap:3}}>
          <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>W:</span>
          <input type="number" min="10" max="500" step="0.1"
            style={{width:60,padding:'3px 5px',fontSize:'.78rem',fontFamily:'monospace',
              background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:4,color:'var(--text)'}}
            value={cardW} onChange={e=>setCardW(parseFloat(e.target.value)||85.6)}/>
          <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>H:</span>
          <input type="number" min="10" max="500" step="0.1"
            style={{width:60,padding:'3px 5px',fontSize:'.78rem',fontFamily:'monospace',
              background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:4,color:'var(--text)'}}
            value={cardH} onChange={e=>setCardH(parseFloat(e.target.value)||53.98)}/>
          <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>mm</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:4}}>
          <span style={{fontSize:'.78rem',color:'var(--text-muted)'}}>BG:</span>
          <input type="color" value={bgColor} onChange={e=>setBgColor(e.target.value)}
            style={{width:28,height:24,padding:1,border:'1px solid var(--border)',borderRadius:4,cursor:'pointer'}}/>
          <button className="btn btn-outline btn-xs" onClick={()=>bgInputRef.current.click()} disabled={bgUploading}>
            {bgUploading ? '⏳ Uploading…' : '📷 BG Image'}
          </button>
          {bgImage && <button className="btn btn-ghost btn-xs" onClick={()=>{setBgImage(null);setBgImagePath(null);}}>✕ BG</button>}
          {bgImagePath && !bgUploading && <span style={{fontSize:'.72rem',color:'var(--success)'}}>✅ Saved</span>}
          <input ref={bgInputRef} type="file" accept="image/*" style={{display:'none'}}
            onChange={async e=>{
              const file = e.target.files[0];
              if (!file) return;
              setBgImage(URL.createObjectURL(file));
              setBgUploading(true);
              try {
                let tmplId = savedTmplId;
                if (!tmplId) {
                  const { data: newTmpl } = await createTemplate({
                    name, bgColor, cardSide, cardWidth: cardW, cardHeight: cardH, elements,
                    targetUsername: targetUsername || undefined,  
                  });
                  tmplId = newTmpl._id;
                  setSavedTmplId(tmplId);
                }
                const { data } = await uploadTemplateBg(tmplId, file);
                setBgImagePath(data.bgImagePath);
                toast.success('Background image saved!');
              } catch {
                toast.error('Background upload failed');
              } finally { setBgUploading(false); }
            }}/>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:4,marginLeft:'auto'}}>
          <span style={{fontSize:'.78rem',color:'var(--text-muted)'}}>Zoom:</span>
          <input type="range" min="0.5" max="2" step="0.1" value={zoom}
            onChange={e=>setZoom(parseFloat(e.target.value))}
            style={{width:80,accentColor:'var(--primary)'}}/>
          <span style={{fontSize:'.78rem',fontFamily:'monospace'}}>{(zoom*100).toFixed(0)}%</span>
        </div>
        <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving?'⏳ Saving…':'💾 Save Template'}
        </button>
      </div>

      {/* ── Main area ─────────────────────────────────────────────────────────── */}
      <div style={{display:'flex',flex:1,overflow:'hidden'}}>

        {/* Left panel — element palette */}
        <div style={{
          width:160, flexShrink:0, borderRight:'1px solid var(--border)',
          background:'var(--surface)', padding:10, overflowY:'auto',
        }}>
          <div style={{fontSize:'.75rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--text-muted)',marginBottom:8}}>
            Elements
          </div>
          {ELEMENT_TYPES.map(et => (
            <button key={et.type} className="btn btn-outline btn-sm"
              style={{width:'100%',marginBottom:6,display:'flex',alignItems:'center',gap:8,justifyContent:'flex-start'}}
              onClick={()=>addElement(et.type)}>
              <span style={{fontSize:'1rem',width:20,textAlign:'center'}}>{et.icon}</span>
              <span style={{fontSize:'.8rem'}}>{et.label}</span>
            </button>
          ))}

          <div style={{fontSize:'.75rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--text-muted)',margin:'14px 0 8px'}}>
            Layers
          </div>
          <div style={{maxHeight:200,overflowY:'auto'}}>
            {[...elements].reverse().map(el=>(
              <div key={el.id}
                style={{
                  padding:'4px 8px',borderRadius:4,marginBottom:2,cursor:'pointer',fontSize:'.78rem',
                  background: el.id===selected?'rgba(var(--primary-rgb),.15)':'var(--bg3)',
                  border: el.id===selected?'1px solid var(--primary)':'1px solid transparent',
                  display:'flex',alignItems:'center',gap:6,
                }}
                onClick={()=>setSelected(el.id)}>
                <span>{ELEMENT_TYPES.find(t=>t.type===el.type)?.icon||'?'}</span>
                <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {el.type==='text'?(el.field||el.staticText||'text'):el.type}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas area */}
        <div
          style={{flex:1, overflow:'auto', background:'#888', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:20}}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={()=>setSelected(null)}
        >
          <div
            ref={canvasRef}
            style={{
              position:'relative',
              width:  `${mmToPx(cardW)}px`,
              height: `${mmToPx(cardH)}px`,
              flexShrink:0,
              background: bgImage ? `url('${bgImage}') center/cover no-repeat` : bgColor,
              boxShadow: '0 4px 24px rgba(0,0,0,.4)',
              cursor:'default',
              overflow:'hidden',
            }}
            onClick={e=>e.stopPropagation()}
          >
            {elements.map(renderElement)}
          </div>
        </div>

        {/* Right panel — properties */}
        <div style={{
          width:220, flexShrink:0, borderLeft:'1px solid var(--border)',
          background:'var(--surface)', padding:10, overflowY:'auto',
        }}>
          {renderProps()}
        </div>
      </div>
    </div>
  );
}